import { FC } from 'react';
import { useSetAtom } from 'jotai';
import type { ChangeMatchPayload } from './types-well';
import { changeMatchAtom } from './atoms/wells';
import { WellPickerPanel } from '../../afe-catalog/structuring/well-picker-panel';

// RC Change Match. Thin RC-specific wrapper over the SHARED `WellPickerPanel`:
// supplies the copy + the RC `changeMatchAtom` mutation, seeds the search with
// the staged well name, and wraps the resolved selection under the `match`
// payload key. POSTs to `resources.matchCandidate.href`. `selectorPageContent`
// anchors the slide-in to the RC shell root.

interface Props {
  open: boolean;
  matchCandidateHref?: string;
  stagedWellName: string;
  onClose: () => void;
  onChanged: () => void;
}

export const ChangeMatchPanel: FC<Props> = ({
  open,
  matchCandidateHref,
  stagedWellName,
  onClose,
  onChanged,
}) => {
  const changeMatch = useSetAtom(changeMatchAtom);

  return (
    <WellPickerPanel
      open={open}
      onClose={onClose}
      title="Change Match"
      subtitle="Search for a different well or create an imported one."
      idPrefix="rc-cm"
      selectorPageContent=".rc-structuring-shell"
      primaryButtonText="Change Match"
      initialSearchValue={stagedWellName}
      onSubmit={(selection, { setPending, setError }) => {
        if (!matchCandidateHref) return;
        // Narrow per branch so the discriminated `ChangeMatchPayload`
        // distributes (TS won't assign `{ match: A | B }` to the union).
        const payload: ChangeMatchPayload =
          selection.kind === 'existing_well'
            ? { match: selection }
            : { match: selection };
        setPending(true);
        setError(null);
        changeMatch({
          url: matchCandidateHref,
          method: 'POST',
          data: payload,
          onSuccess: () => {
            setPending(false);
            onChanged();
          },
          onError: (m) => {
            setPending(false);
            setError(m ?? 'Failed to change match.');
          },
        });
      }}
    />
  );
};
